import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DimensionType,
  DIMENSION_LABELS,
  MetricType,
  METRIC_LABELS,
  LiquidationFilter,
  ExceptionFilter,
  TimePeriodType,
  BreadcrumbItem,
  SortConfig,
  AnalyticsSettings,
} from "../types";

// --- Defaults ---

const DEFAULT_DIMENSION: DimensionType = "month";
const DEFAULT_METRICS: MetricType[] = ["revenue", "quantity"];
const DEFAULT_LIQUIDATION: LiquidationFilter = "all";
const DEFAULT_EXCEPTION: ExceptionFilter = "all";
const DEFAULT_TIME_PERIOD: TimePeriodType = "ytd";

// --- Type guards ---

const VALID_DIMENSIONS = new Set(Object.keys(DIMENSION_LABELS));
const VALID_METRICS = new Set(Object.keys(METRIC_LABELS));
const VALID_LIQUIDATION = new Set<string>(["all", "only", "none"]);
const VALID_EXCEPTION = new Set<string>(["all", "only", "none"]);
const VALID_TIME_PERIODS = new Set<string>(["ytd", "periodic"]);
const VALID_SORT_DIRECTIONS = new Set<string>(["asc", "desc"]);

function isValidDimension(value: string): value is DimensionType {
  return VALID_DIMENSIONS.has(value);
}

function isValidMetric(value: string): value is MetricType {
  return VALID_METRICS.has(value);
}

function isValidLiquidation(value: string): value is LiquidationFilter {
  return VALID_LIQUIDATION.has(value);
}

function isValidException(value: string): value is ExceptionFilter {
  return VALID_EXCEPTION.has(value);
}

function isValidTimePeriod(value: string): value is TimePeriodType {
  return VALID_TIME_PERIODS.has(value);
}

// --- Breadcrumb serialization ---
// Format: dimension:value:label per item, items joined by ~
// Each segment is encodeURIComponent-encoded

function serializeFilters(filters: BreadcrumbItem[]): string | null {
  if (filters.length === 0) return null;
  return filters
    .map(
      (f) =>
        `${encodeURIComponent(f.dimension)}:${encodeURIComponent(f.value)}:${encodeURIComponent(f.label)}`,
    )
    .join("~");
}

function deserializeFilters(param: string | null): BreadcrumbItem[] {
  if (!param) return [];
  return param
    .split("~")
    .map((triple) => {
      const parts = triple.split(":");
      if (parts.length < 3) return null;
      const dimension = decodeURIComponent(parts[0]);
      const value = decodeURIComponent(parts[1]);
      // Label may contain encoded colons, rejoin remaining parts
      const label = decodeURIComponent(parts.slice(2).join(":"));
      if (!isValidDimension(dimension)) return null;
      return { dimension, value, label } as BreadcrumbItem;
    })
    .filter((item): item is BreadcrumbItem => item !== null);
}

// --- Sort serialization ---
// Format: column:direction

function serializeSortConfig(sort: SortConfig | null): string | null {
  if (!sort) return null;
  return `${sort.column}:${sort.direction}`;
}

function deserializeSortConfig(param: string | null): SortConfig | null {
  if (!param) return null;
  const lastColon = param.lastIndexOf(":");
  if (lastColon === -1) return null;
  const column = param.slice(0, lastColon);
  const direction = param.slice(lastColon + 1);
  if (!column || !VALID_SORT_DIRECTIONS.has(direction)) return null;
  return { column, direction: direction as "asc" | "desc" };
}

// --- Hook ---

interface UseFiltersResult {
  applyAnalyticsSettings: (settings: AnalyticsSettings) => void;
  selectedMetrics: MetricType[];
  setSelectedMetrics: (metrics: MetricType[]) => void;
  currentDimension: DimensionType;
  setCurrentDimension: (dimension: DimensionType) => void;
  filters: BreadcrumbItem[];
  addFilter: (filter: BreadcrumbItem) => void;
  navigateToFilter: (index: number) => void;
  clearFilters: () => void;
  liquidationFilter: LiquidationFilter;
  setLiquidationFilter: (filter: LiquidationFilter) => void;
  exceptionFilter: ExceptionFilter;
  setExceptionFilter: (filter: ExceptionFilter) => void;
  timePeriod: TimePeriodType;
  setTimePeriod: (period: TimePeriodType) => void;
  selectedCountries: string[];
  setSelectedCountries: (countries: string[]) => void;
  selectedSalesPeople: string[];
  setSelectedSalesPeople: (salesPeople: string[]) => void;
  sortConfig: SortConfig | null;
  handleSort: (column: string) => void;
}

export function useFilters(): UseFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper: update params with push (new history entry)
  const updateParams = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          updater(next);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  // Helper: update params with replace (no history entry)
  const replaceParams = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          updater(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // --- Read state from URL ---

  const currentDimension = useMemo<DimensionType>(() => {
    const raw = searchParams.get("dim");
    return raw && isValidDimension(raw) ? raw : DEFAULT_DIMENSION;
  }, [searchParams]);

  const selectedMetrics = useMemo<MetricType[]>(() => {
    const raw = searchParams.get("metrics");
    if (!raw) return DEFAULT_METRICS;
    const parsed = raw.split(",").filter(isValidMetric);
    return parsed.length > 0 ? parsed : DEFAULT_METRICS;
  }, [searchParams]);

  const filters = useMemo<BreadcrumbItem[]>(
    () => deserializeFilters(searchParams.get("f")),
    [searchParams],
  );

  const liquidationFilter = useMemo<LiquidationFilter>(() => {
    const raw = searchParams.get("liq");
    return raw && isValidLiquidation(raw) ? raw : DEFAULT_LIQUIDATION;
  }, [searchParams]);

  const exceptionFilter = useMemo<ExceptionFilter>(() => {
    const raw = searchParams.get("exc");
    return raw && isValidException(raw) ? raw : DEFAULT_EXCEPTION;
  }, [searchParams]);

  const timePeriod = useMemo<TimePeriodType>(() => {
    const raw = searchParams.get("period");
    return raw && isValidTimePeriod(raw) ? raw : DEFAULT_TIME_PERIOD;
  }, [searchParams]);

  const selectedCountries = useMemo<string[]>(() => {
    const raw = searchParams.get("countries");
    if (!raw) return [];
    return raw.split(",").filter(Boolean);
  }, [searchParams]);

  const selectedSalesPeople = useMemo<string[]>(() => {
    const raw = searchParams.get("salesPeople");
    if (!raw) return [];
    return raw.split(",").filter(Boolean);
  }, [searchParams]);

  const sortConfig = useMemo<SortConfig | null>(
    () => deserializeSortConfig(searchParams.get("sort")),
    [searchParams],
  );

  // --- Write state to URL ---

  const applyAnalyticsSettings = useCallback(
    (settings: AnalyticsSettings) => {
      replaceParams((params) => {
        const values: [string, string, string][] = [
          [
            "metrics",
            settings.selectedMetrics.join(","),
            DEFAULT_METRICS.join(","),
          ],
          ["liq", settings.liquidationFilter, DEFAULT_LIQUIDATION],
          ["exc", settings.exceptionFilter, DEFAULT_EXCEPTION],
          ["period", settings.timePeriod, DEFAULT_TIME_PERIOD],
          ["countries", settings.selectedCountries.join(","), ""],
          ["salesPeople", settings.selectedSalesPeople.join(","), ""],
        ];
        for (const [key, value, defaultValue] of values) {
          if (value === defaultValue) {
            params.delete(key);
          } else {
            params.set(key, value);
          }
        }
      });
    },
    [replaceParams],
  );

  const setCurrentDimension = useCallback(
    (dimension: DimensionType) => {
      updateParams((p) => {
        if (dimension === DEFAULT_DIMENSION) {
          p.delete("dim");
        } else {
          p.set("dim", dimension);
        }
      });
    },
    [updateParams],
  );

  const setSelectedMetrics = useCallback(
    (metrics: MetricType[]) => {
      replaceParams((p) => {
        const val = metrics.join(",");
        if (val === DEFAULT_METRICS.join(",")) {
          p.delete("metrics");
        } else {
          p.set("metrics", val);
        }
      });
    },
    [replaceParams],
  );

  const addFilter = useCallback(
    (filter: BreadcrumbItem) => {
      updateParams((p) => {
        const current = deserializeFilters(p.get("f"));
        const updated = [...current, filter];
        const serialized = serializeFilters(updated);
        if (serialized) {
          p.set("f", serialized);
        }
      });
    },
    [updateParams],
  );

  const navigateToFilter = useCallback(
    (index: number) => {
      updateParams((p) => {
        if (index < 0) {
          p.delete("f");
        } else {
          const current = deserializeFilters(p.get("f"));
          const sliced = current.slice(0, index + 1);
          const serialized = serializeFilters(sliced);
          if (serialized) {
            p.set("f", serialized);
          } else {
            p.delete("f");
          }
        }
      });
    },
    [updateParams],
  );

  const clearFilters = useCallback(() => {
    updateParams((p) => {
      p.delete("f");
    });
  }, [updateParams]);

  const setLiquidationFilter = useCallback(
    (filter: LiquidationFilter) => {
      replaceParams((p) => {
        if (filter === DEFAULT_LIQUIDATION) {
          p.delete("liq");
        } else {
          p.set("liq", filter);
        }
      });
    },
    [replaceParams],
  );

  const setExceptionFilter = useCallback(
    (filter: ExceptionFilter) => {
      replaceParams((params) => {
        if (filter === DEFAULT_EXCEPTION) {
          params.delete("exc");
        } else {
          params.set("exc", filter);
        }
      });
    },
    [replaceParams],
  );

  const setTimePeriod = useCallback(
    (period: TimePeriodType) => {
      replaceParams((p) => {
        if (period === DEFAULT_TIME_PERIOD) {
          p.delete("period");
        } else {
          p.set("period", period);
        }
      });
    },
    [replaceParams],
  );

  const setSelectedCountries = useCallback(
    (countries: string[]) => {
      replaceParams((p) => {
        if (countries.length === 0) {
          p.delete("countries");
        } else {
          p.set("countries", countries.join(","));
        }
      });
    },
    [replaceParams],
  );

  const setSelectedSalesPeople = useCallback(
    (salesPeople: string[]) => {
      replaceParams((p) => {
        if (salesPeople.length === 0) {
          p.delete("salesPeople");
        } else {
          p.set("salesPeople", salesPeople.join(","));
        }
      });
    },
    [replaceParams],
  );

  const handleSort = useCallback(
    (column: string) => {
      replaceParams((p) => {
        const current = deserializeSortConfig(p.get("sort"));
        let next: SortConfig | null;
        if (current?.column === column) {
          next =
            current.direction === "asc" ? { column, direction: "desc" } : null;
        } else {
          next = { column, direction: "asc" };
        }
        const serialized = serializeSortConfig(next);
        if (serialized) {
          p.set("sort", serialized);
        } else {
          p.delete("sort");
        }
      });
    },
    [replaceParams],
  );

  return {
    applyAnalyticsSettings,
    selectedMetrics,
    setSelectedMetrics,
    currentDimension,
    setCurrentDimension,
    filters,
    addFilter,
    navigateToFilter,
    clearFilters,
    liquidationFilter,
    setLiquidationFilter,
    exceptionFilter,
    setExceptionFilter,
    timePeriod,
    setTimePeriod,
    selectedCountries,
    setSelectedCountries,
    selectedSalesPeople,
    setSelectedSalesPeople,
    sortConfig,
    handleSort,
  };
}
